import type { LookalikePersona } from '@repo/contracts';
import {
  LOOKALIKE_WEIGHT_FIELDS,
  type LookalikeWeightField,
} from '@repo/contracts/lookalike-schema';

/**
 * The one place the schema's naming translates into a sampled lookalike's:
 * the schema calls a field `companySizes` (a pool of candidates); a
 * lookalike carries one value from that pool under the singular
 * `companySize`. Every UI that needs "the categorical fields" — the filter
 * dropdown, the field-view axis dropdowns, the weight sliders' labels —
 * derives its list from `LOOKALIKE_WEIGHT_FIELDS` through this map, instead
 * of each keeping its own copy that can silently drift from the schema.
 *
 * `satisfies Record<LookalikeWeightField, …>` does two things: forces every
 * schema field to be covered here (a new one fails to compile until it is),
 * and — kept as `const` rather than widened by the `Record` annotation —
 * keeps each `personaField` a string *literal*, which is what lets
 * `CategoricalPersonaField` below be a real union instead of plain `string`.
 */
const CATEGORICAL_FIELD_INFO = {
  brandLoyalty: { label: 'Brand loyalty', personaField: 'brandLoyalty' },
  companySizes: { label: 'Company size', personaField: 'companySize' },
  goals: { label: 'Goal', personaField: 'goal' },
  jobTitles: { label: 'Job title', personaField: 'jobTitle' },
  locations: { label: 'Location', personaField: 'location' },
  painPoints: { label: 'Pain point', personaField: 'painPoint' },
  techSavviness: { label: 'Tech savviness', personaField: 'techSavviness' },
} as const satisfies Record<
  LookalikeWeightField,
  { label: string; personaField: keyof LookalikePersona }
>;

/** Every categorical `LookalikePersona` key — `'jobTitle' | 'companySize' |
 *  'location' | 'painPoint' | 'goal' | 'techSavviness' | 'brandLoyalty'` —
 *  derived, not hand-typed, so it can never list a field the map above
 *  doesn't also cover. */
export type CategoricalPersonaField =
  (typeof CATEGORICAL_FIELD_INFO)[LookalikeWeightField]['personaField'];

export interface CategoricalField {
  label: string;
  personaField: CategoricalPersonaField;
  schemaField: LookalikeWeightField;
}

/** Every categorical field, in the schema's own order. */
export const CATEGORICAL_FIELDS: CategoricalField[] =
  LOOKALIKE_WEIGHT_FIELDS.map((schemaField) => ({
    schemaField,
    ...CATEGORICAL_FIELD_INFO[schemaField],
  }));

/** The schema pool key for a categorical persona field — the inverse of
 *  `CATEGORICAL_FIELD_INFO`. */
export function schemaFieldFor(
  personaField: CategoricalPersonaField,
): LookalikeWeightField {
  const found = CATEGORICAL_FIELDS.find((f) => f.personaField === personaField);
  // Exhaustive by construction: CategoricalPersonaField only ever contains
  // values CATEGORICAL_FIELDS was built from, so this can't actually miss —
  // the check exists for the type system, not because it's reachable.
  if (!found) throw new Error(`Unknown categorical field: ${personaField}`);
  return found.schemaField;
}

/** A lookalike's own value for a categorical field, read generically — every
 *  `CategoricalPersonaField` names a `LookalikePersona` property whose value
 *  is always a string (`LookalikeSavviness` is itself a string union), so
 *  this cast is sound for every field this module lists. */
export function categoricalValueOf(
  lookalike: LookalikePersona,
  personaField: CategoricalPersonaField,
): string {
  return lookalike[personaField as keyof LookalikePersona] as string;
}
